import { useEffect, useState } from 'react';
import { Modal, ModalBody, Button, FormGroup, Label, Input, FormFeedback, InputGroup, InputGroupText } from 'reactstrap';

/**
 * Small confirm dialog with a few optional fields (reason, amount, note).
 *
 * fields: [{ name, label, type: 'text'|'textarea'|'number', required, placeholder, defaultValue, prefix, hint }]
 * onConfirm(values) may return a promise; the dialog shows a busy state until it settles.
 */
const ActionDialog = ({
  isOpen,
  title,
  description,
  fields = [],
  confirmLabel = 'Confirm',
  confirmColor = 'primary',
  onConfirm,
  onClose,
  zIndex = 1070,
}) => {
  const [values, setValues] = useState({});
  const [touched, setTouched] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const init = {};
      fields.forEach((f) => {
        init[f.name] = f.defaultValue !== undefined && f.defaultValue !== null ? String(f.defaultValue) : '';
      });
      setValues(init);
      setTouched({});
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const missing = fields.filter((f) => f.required && !String(values[f.name] || '').trim());
  const canConfirm = missing.length === 0 && !busy;

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!canConfirm) {
      const t = {};
      fields.forEach((f) => {
        t[f.name] = true;
      });
      setTouched(t);
      return;
    }
    try {
      setBusy(true);
      await onConfirm(values);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={busy ? undefined : onClose} centered size="md" zIndex={zIndex}>
      <form onSubmit={submit} noValidate>
        <div className="pm-modal-header">
          <h5>{title}</h5>
        </div>
        <ModalBody>
          {description && <p className="text-muted small mb-3">{description}</p>}
          {fields.map((f) => {
            const val = values[f.name] ?? '';
            const invalid = f.required && touched[f.name] && !String(val).trim();
            const inputId = `pm-ad-${f.name}`;
            const control = (
              <Input
                id={inputId}
                type={f.type || 'text'}
                value={val}
                placeholder={f.placeholder || ''}
                rows={f.type === 'textarea' ? 3 : undefined}
                step={f.type === 'number' ? '0.01' : undefined}
                min={f.type === 'number' ? '0' : undefined}
                invalid={invalid}
                disabled={busy}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                onBlur={() => setTouched((t) => ({ ...t, [f.name]: true }))}
              />
            );
            return (
              <FormGroup key={f.name}>
                <Label for={inputId}>
                  {f.label}
                  {f.required && <span className="text-danger ms-1">*</span>}
                </Label>
                {f.prefix ? (
                  <InputGroup>
                    <InputGroupText>{f.prefix}</InputGroupText>
                    {control}
                    {invalid && <FormFeedback>{f.label} is required</FormFeedback>}
                  </InputGroup>
                ) : (
                  <>
                    {control}
                    {invalid && <FormFeedback>{f.label} is required</FormFeedback>}
                  </>
                )}
                {f.hint && <div className="form-text">{f.hint}</div>}
              </FormGroup>
            );
          })}
        </ModalBody>
        <div className="pm-modal-footer justify-content-end">
          <Button type="button" color="light" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" color={confirmColor} disabled={!canConfirm}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export { ActionDialog };
export default ActionDialog;
